import { Box, Button, Flex, Stack, Text } from '@chakra-ui/react';
import { BookOpenText, X } from '@phosphor-icons/react';
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle
} from '@/components/ui/dialog';

interface WelcomeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onGetStarted: () => void;
}

export function WelcomeDialog({ isOpen, onClose, onGetStarted }: WelcomeDialogProps) {
  const handleDocumentationClick = () => {
    window.open('https://www.polarisjobs.com/documentation', '_blank');
  };

  return (
    <DialogRoot
      role="dialog"
      placement="center"
      size="lg"
      open={isOpen}
      onOpenChange={({ open }) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent borderRadius="12px" maxW="600px">
        <DialogHeader>
          <DialogTitle>
            <Flex alignItems="center" gap="12px">
              <Box
                w="48px"
                h="48px"
                borderRadius="12px"
                bg="primary.50"
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="primary.900"
              >
                <BookOpenText size={24} />
              </Box>
              <Text fontSize="28px" fontWeight="bold" color="neutrals.900">
                Welcome to PolarisJobs! 🎉
              </Text>
            </Flex>
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Box borderBottom="1px solid" borderColor="neutrals.100" mb="24px" />
          <Stack gap="24px">
            <Text fontSize="18px" color="neutrals.700" lineHeight="1.6">
              We're excited to have you on board! Get up and running in minutes and make the most of
              PolarisJobs with our quick start tutorial.
            </Text>

            <Box
              bg="primary.50"
              border="1px solid"
              borderColor="primary.200"
              borderRadius="12px"
              p="20px"
              cursor="pointer"
              onClick={handleDocumentationClick}
              _hover={{ cursor: 'pointer', boxShadow: 'sm' }}
              transition="all 0.2s ease-in-out"
            >
              <Flex alignItems="flex-start" gap="12px">
                <Box
                  w="32px"
                  h="32px"
                  borderRadius="8px"
                  bg="primary.100"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                  mt="2px"
                >
                  <BookOpenText size={16} color="#4E76FF" />
                </Box>
                <Box>
                  <Text fontSize="16px" fontWeight="semibold" color="neutrals.900" mb="8px">
                    Quick Start Tutorial
                  </Text>
                  <Text fontSize="14px" color="neutrals.600" lineHeight="1.5">
                    Learn how to create your first job posting, manage candidates, and conduct
                    AI-powered interviews in just a few minutes.
                  </Text>
                </Box>
              </Flex>
            </Box>
          </Stack>
        </DialogBody>
        <DialogFooter gap="12px">
          <Button
            bg="primary.900"
            fontSize="16px"
            borderRadius="8px"
            onClick={handleDocumentationClick}
            _hover={{ bg: 'primary.700' }}
          >
            <BookOpenText size={20} />
            Read Tutorial
          </Button>
          <Button
            bg="neutrals.900"
            fontSize="16px"
            borderRadius="8px"
            onClick={onGetStarted}
            _hover={{ bg: 'neutrals.700' }}
          >
            Get Started
          </Button>
        </DialogFooter>
        <DialogCloseTrigger m="12px">
          <X size={24} weight="bold" />
        </DialogCloseTrigger>
      </DialogContent>
    </DialogRoot>
  );
}
